import React, {useEffect} from "react";
import { useClassicView } from "../../../../config/classicView";
import { ExportExcelButton } from "../../../../utils/exportButton";

// types
import { Order } from "../../../../types/orders/order";
import { DateRange } from "../../../../types/common";
import {BASE_URL} from "../../../../redux/actions/dashboard/env.variables";

// components
import OrdersContent from "./Orders.content";
import OrdersView from "./OrdersView";
import {generateExportButton} from "../../../../utils/reactFunctions";
import {dateToShortDateString, fetchBlob, getDefaultDates, setCurrentTab} from "../../../../utils/functions";
import store from "../../../../redux/store";

interface Props {
  orders: Order[];
  fetchingOrders: boolean;
  getOrdersRequest: (data: any, params: any) => Promise<void>;
  // filters
  rangeDate: DateRange;
  activeFilters: any;
  sendFilters: () => void;
  take: number;
  skip: number;
  total: number;
  featuresAllowed: string[];
}

export default (props: Props) => {
  const dateSince = dateToShortDateString(props.rangeDate.start) || getDefaultDates().start;
  const dateUntil = dateToShortDateString(props.rangeDate.end) || getDefaultDates().end;

  useEffect(() => {
    setCurrentTab('orders');

    const dataQuery = {
      filter: props.activeFilters.filter,
      skip: props.skip,
      take: props.take,
    };
    const params = {
      dateSince, dateUntil
    };
    props.getOrdersRequest(dataQuery, params);
  }, []);

  const downloadCsvZip = (params: any) => {
    try {
      console.log("Iniciando la descarga del archivo CSV.ZIP...");
      console.log("Parámetros iniciales:", params);

      // Construye los parámetros query
      const queryParams = new URLSearchParams({
        entity: params.query.entity,
        format: params.query.format,
        dateSince: params.query.dateSince,
        dateUntil: params.query.dateUntil,
      });

      // URL completa con parámetros query
      const apiUrl = `${BASE_URL}/api/Orders?${queryParams.toString()}`;
      console.log("URL construida para solicitud al API:", apiUrl);

      // Obtener el token JWT desde el store o localStorage
      const token = store.getState().auth.user?.token || localStorage.getItem("token");
      if (!token) {
        throw new Error("No se encontró un token JWT.");
      }
      console.log("Token JWT encontrado:", token);

      // Crear una nueva instancia de XMLHttpRequest
      const xhr = new XMLHttpRequest();
      xhr.open("POST", apiUrl, true); // Método `POST` con solicitud asíncrona
      xhr.responseType = "blob"; // Especificamos que esperamos un archivo binario (Blob)

      // Configurar cabeceras
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.setRequestHeader("Content-Type", "application/json-patch+json");
      console.log("Cabeceras configuradas: Authorization y Content-Type.");

      // Manejar la respuesta del servidor
      xhr.onload = () => {
        if (xhr.status === 200) {
          console.log("Respuesta del servidor exitosa:", xhr.status);

          // Procesar la respuesta como Blob
          const blob = new Blob([xhr.response], { type: "application/zip" });
          const url = window.URL.createObjectURL(blob);

          // Crear enlace dinámico para descargar con extensión `.csv.zip`
          const a = document.createElement("a");
          a.href = url;
          a.download = "archivo.csv.zip"; // Nombre del archivo descargado
          document.body.appendChild(a);
          a.click();

          // Limpiar recursos
          a.remove();
          window.URL.revokeObjectURL(url);
          console.log("Descarga completada exitosamente.");
        } else {
          console.error("Error en la respuesta del servidor. Estado:", xhr.status);
          console.error("Respuesta del servidor:", xhr.responseText);
        }
      };

      // Manejar errores de red
      xhr.onerror = () => {
        console.error("Error durante la solicitud al servidor.");
      };

      // Enviar la solicitud con los filtros activos (filter/sort) en el body.
      // El backend (POST /api/Orders) los lee como KendoGridSearchRequest y aplica
      // el filtrado; para CSV ignora skip/take (los fuerza a 0 / 1.000.000).
      const requestBody = params.body?.filters ?? {};
      console.log("Enviando solicitud con filtros:", requestBody);
      xhr.send(JSON.stringify(requestBody));
    } catch (error) {
      console.error("Error durante la descarga del archivo CSV.ZIP:", error);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    let params = {
      query: {
        dateSince,
        dateUntil,
        format: "csv",
        entity: "orders"
      },
      body: {
        filters: props.activeFilters
      }
    }
    event.preventDefault(); // Previene el refresco de la página
    downloadCsvZip(params); // Llama a la función de descarga
  };

  const exportCsvButton = () => <ExportExcelButton onClick={handleClick} />;

  const { classic } = useClassicView();
  const Content = classic ? OrdersContent : OrdersView;

  return (
      <Content
          tableData={props.orders}
          fetchingOrders={props.fetchingOrders}
          exportButton={exportCsvButton}
          sendFilters={props.sendFilters}
          take={props.take}
          skip={props.skip}
          total={props.total}
          featuresAllowed={props.featuresAllowed}
      />
  );
};
